// src/components/Sales/Sales.js - Combined Amazon + Shopify Sales Dashboard
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  Home,
  RefreshCw,
  Package,
  DollarSign,
  ShoppingCart,
  Calendar,
  ArrowLeft,
  Filter,
  Store,
  Globe,
  Users,
  Eye
} from 'lucide-react';

const Sales = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('combined'); // 'combined', 'amazon', 'shopify'

  // Modal states for orders detail
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayOrders, setDayOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Amazon data
  const [amazonStats, setAmazonStats] = useState({
    total_orders: 0,
    total_units: 0,
    total_revenue: 0
  });
  const [amazonByRegion, setAmazonByRegion] = useState([]);
  const [amazonDailySales, setAmazonDailySales] = useState([]);

  // Shopify data
  const [shopifyStats, setShopifyStats] = useState({
    total_orders: 0,
    total_revenue: 0,
    unique_customers: 0,
    avg_order_value: 0
  });
  const [shopifyDailySales, setShopifyDailySales] = useState([]);
  const [shopifyTopProducts, setShopifyTopProducts] = useState([]);

  // Filters
  const [daysFilter, setDaysFilter] = useState(30);
  const [regionFilter, setRegionFilter] = useState('ALL');

  // Amazon Detailed View states
  const [amazonDetailedData, setAmazonDetailedData] = useState(null);
  const [showAmazonDetails, setShowAmazonDetails] = useState(false);

  const fetchAmazonDetailedData = async (region = 'ALL') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/amazon/sales/detailed?days=${daysFilter}&region=${region}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAmazonDetailedData(data);
          setShowAmazonDetails(true);
        }
      }
    } catch (error) {
      console.error('Error fetching Amazon details:', error);
    }
  };

  // Fetch orders for specific day
const fetchDayOrders = async (date) => {
  try {
    setLoadingOrders(true);

    const from = `${date}T00:00:00`;
    const to = `${date}T23:59:59`;

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/shopify/orders/by-date?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        setDayOrders(data.orders);
      }
    }
  } catch (error) {
    console.error('❌ Error fetching orders:', error);
  } finally {
    setLoadingOrders(false);
  }
};


  const handleShowOrders = (date) => {
    setSelectedDate(date);
    setShowOrdersModal(true);
    fetchDayOrders(date);
  };

  // Fetch Amazon sales stats
const fetchAmazonSales = async (days = 30, region = 'ALL') => {
  try {
    let params = {};

    if (useCustomRange && customRange.from && customRange.to) {
      let from = customRange.from;
      let to = customRange.to;
      if (from === to) {
        from = `${from}T00:00:00`;
        to = `${to}T23:59:59`;
      }
      params = { from, to, ...(region !== 'ALL' && { region }) };
    } else {
      params = { days, ...(region !== 'ALL' && { region }) };
    }

    const queryParams = new URLSearchParams(params);

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/amazon/sales/stats?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) throw new Error('Failed to fetch Amazon sales stats');
    const data = await response.json();

    if (data.success) {
      const totalOrders = data.stats.reduce((sum, r) => sum + (r.TOTAL_ORDERS || 0), 0);
      const totalUnits = data.stats.reduce((sum, r) => sum + (r.TOTAL_UNITS || 0), 0);
      const totalRevenue = data.stats.reduce((sum, r) => sum + (r.TOTAL_REVENUE || 0), 0);

      setAmazonStats({
        total_orders: totalOrders,
        total_units: totalUnits,
        total_revenue: totalRevenue
      });
      setAmazonByRegion(data.stats);
    }

    const dailyResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/amazon/sales/daily?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (dailyResponse.ok) {
      const dailyData = await dailyResponse.json();
      if (dailyData.success) {
        setAmazonDailySales(dailyData.sales);
      }
    }
  } catch (error) {
    console.error('❌ Error fetching Amazon sales:', error);
  }
};


  // Fetch Shopify sales

const fetchShopifySales = async (days = 30) => {
  try {
    let params = {};

    if (useCustomRange && customRange.from && customRange.to) {
      let from = customRange.from;
      let to = customRange.to;
      if (from === to) {
        from = `${from}T00:00:00`;
        to = `${to}T23:59:59`;
      }
      params = { from, to };
    } else {
      params = { days };
    }

    const queryParams = new URLSearchParams(params);

    const summaryResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/shopify/summary?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (summaryResponse.ok) {
      const summaryData = await summaryResponse.json();
      if (summaryData.success) {
        setShopifyStats({
          total_orders: summaryData.summary.TOTAL_ORDERS || 0,
          total_revenue: summaryData.summary.TOTAL_REVENUE || 0,
          unique_customers: summaryData.summary.UNIQUE_CUSTOMERS || 0,
          avg_order_value: summaryData.summary.AVG_ORDER_VALUE || 0
        });
      }
    }

    const dailyResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/shopify/daily?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (dailyResponse.ok) {
      const dailyData = await dailyResponse.json();
      if (dailyData.success) {
        setShopifyDailySales(dailyData.sales);
      }
    }

    const productsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/shopify/products?limit=10&${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (productsResponse.ok) {
      const productsData = await productsResponse.json();
      if (productsData.success) {
        setShopifyTopProducts(productsData.products);
      }
    }
  } catch (error) {
    console.error('❌ Error fetching Shopify sales:', error);
  }
};


  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [useCustomRange, setUseCustomRange] = useState(false);


  // Load all data
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      setError('');

      // Fetch both Amazon and Shopify with current filters
      await Promise.all([
        fetchAmazonSales(daysFilter, regionFilter),
        fetchShopifySales(daysFilter)
      ]);

      setLoading(false);
    };

    loadAllData();
  }, [daysFilter, regionFilter]);

  // Calculate combined stats
  const combinedStats = {
    total_orders: amazonStats.total_orders + shopifyStats.total_orders,
    total_revenue: amazonStats.total_revenue + shopifyStats.total_revenue,
    amazon_percentage: amazonStats.total_revenue && (amazonStats.total_revenue + shopifyStats.total_revenue) > 0
      ? (amazonStats.total_revenue / (amazonStats.total_revenue + shopifyStats.total_revenue) * 100).toFixed(1)
      : 0,
    shopify_percentage: shopifyStats.total_revenue && (amazonStats.total_revenue + shopifyStats.total_revenue) > 0
      ? (shopifyStats.total_revenue / (amazonStats.total_revenue + shopifyStats.total_revenue) * 100).toFixed(1)
      : 0
  };

  const OrdersModal = () => {
    if (!showOrdersModal) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '95%',
          maxWidth: '1400px',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Modal Header */}
          <div style={{
            padding: '1.5rem',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{margin: 0, fontSize: '1.5rem', fontWeight: '600'}}>
                Shopify Orders - {selectedDate && new Date(selectedDate).toLocaleDateString()}
              </h2>
              <p style={{margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.875rem'}}>
                {dayOrders.length} orders found
              </p>
            </div>
            <button
              onClick={() => setShowOrdersModal(false)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#f3f4f6',
                cursor: 'pointer',
                fontSize: '1.25rem',
                fontWeight: '500'
              }}
            >
              ✕
            </button>
          </div>

          {/* Modal Body */}
          <div style={{
            flex: 1,
            padding: '1.5rem',
            overflowY: 'auto'
          }}>
            {loadingOrders ? (
              <div style={{textAlign: 'center', padding: '2rem'}}>
                <div className="loading-spinner" style={{margin: '0 auto'}} />
                <p style={{marginTop: '1rem', color: '#6b7280'}}>Loading orders...</p>
              </div>
            ) : (
              <div style={{overflowX: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem'}}>
                  <thead>
                    <tr style={{borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb'}}>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', whiteSpace: 'nowrap'}}>Order #</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Customer</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Products</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', whiteSpace: 'nowrap'}}>Items</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', whiteSpace: 'nowrap'}}>Total</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Payment</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Fulfillment</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayOrders.map((order, index) => (
                      <tr key={index} style={{borderBottom: '1px solid #f3f4f6', hover: {backgroundColor: '#f9fafb'}}}>
                        <td style={{padding: '0.75rem', whiteSpace: 'nowrap'}}>
                          <span style={{
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            backgroundColor: '#e0e7ff',
                            color: '#3730a3',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>
                            #{order.ORDER_NUMBER || order.ID || index + 1}
                          </span>
                        </td>
                        <td style={{padding: '0.75rem'}}>
                          <div>
                            <div style={{fontWeight: '500'}}>
                              {order.CUSTOMER_NAME?.trim() || 'Guest'}
                            </div>
                            <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                              {order.EMAIL || '-'}
                            </div>
                          </div>
                        </td>
                        <td style={{padding: '0.75rem', maxWidth: '350px'}}>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.25rem'
                          }}>
                            {order.PRODUCTS_LIST ? (
                              order.PRODUCTS_LIST.split(', ').map((product, idx) => (
                                <span key={idx} style={{
                                  backgroundColor: '#f3f4f6',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  border: '1px solid #e5e7eb',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {product}
                                </span>
                              ))
                            ) : (
                              <span style={{color: '#9ca3af', fontSize: '0.75rem'}}>No products data</span>
                            )}
                          </div>
                          {order.SKUS_LIST && (
                            <div style={{
                              marginTop: '0.25rem',
                              fontSize: '0.625rem',
                              color: '#6b7280',
                              fontFamily: 'monospace'
                            }}>
                              SKUs: {order.SKUS_LIST}
                            </div>
                          )}
                        </td>
                        <td style={{padding: '0.75rem', textAlign: 'center'}}>
                          <span style={{
                            backgroundColor: '#dbeafe',
                            color: '#1e40af',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '999px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            {order.TOTAL_ITEMS || order.LINE_ITEMS_COUNT || 0}
                          </span>
                        </td>
                        <td style={{padding: '0.75rem', whiteSpace: 'nowrap'}}>
                          <div>
                            <div style={{fontWeight: '600', color: '#059669'}}>
                              ${parseFloat(order.TOTAL_PRICE || 0).toFixed(2)}
                            </div>
                            {order.TOTAL_DISCOUNTS > 0 && (
                              <div style={{fontSize: '0.625rem', color: '#dc2626'}}>
                                -${parseFloat(order.TOTAL_DISCOUNTS).toFixed(2)} discount
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: order.FINANCIAL_STATUS === 'paid' ? '#dcfce7' :
                                           order.FINANCIAL_STATUS === 'pending' ? '#fef3c7' : '#fee2e2',
                            color: order.FINANCIAL_STATUS === 'paid' ? '#166534' :
                                  order.FINANCIAL_STATUS === 'pending' ? '#92400e' : '#991b1b',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}>
                            {order.FINANCIAL_STATUS || 'pending'}
                          </span>
                        </td>
                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: order.FULFILLMENT_STATUS === 'fulfilled' ? '#dcfce7' :
                                           order.FULFILLMENT_STATUS === 'partial' ? '#fef3c7' : '#f3f4f6',
                            color: order.FULFILLMENT_STATUS === 'fulfilled' ? '#166534' :
                                  order.FULFILLMENT_STATUS === 'partial' ? '#92400e' : '#6b7280',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}>
                            {order.FULFILLMENT_STATUS || 'unfulfilled'}
                          </span>
                        </td>
                        <td style={{padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap'}}>
                          {new Date(order.CREATED_AT).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {dayOrders.length === 0 && (
                  <div style={{textAlign: 'center', padding: '2rem', color: '#6b7280'}}>
                    No orders found for this date
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{display: 'flex', gap: '2rem', fontSize: '0.875rem'}}>
              <div>
                <span style={{color: '#6b7280'}}>Total Orders:</span>{' '}
                <span style={{fontWeight: '600'}}>{dayOrders.length}</span>
              </div>
              <div>
                <span style={{color: '#6b7280'}}>Total Revenue:</span>{' '}
                <span style={{fontWeight: '600', color: '#059669'}}>
                  ${dayOrders.reduce((sum, order) => sum + parseFloat(order.TOTAL_PRICE || 0), 0).toFixed(2)}
                </span>
              </div>
              <div>
                <span style={{color: '#6b7280'}}>Total Items:</span>{' '}
                <span style={{fontWeight: '600'}}>
                  {dayOrders.reduce((sum, order) => sum + parseInt(order.TOTAL_ITEMS || 0), 0)}
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowOrdersModal(false)}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem'}}>
              <Link to="/dashboard" className="btn-secondary" style={{minWidth: 'auto', padding: '0.5rem'}}>
                <ArrowLeft size={16} />
              </Link>
              <h1 className="header-title">
                Sales Dashboard
              </h1>
            </div>
            <p className="header-subtitle">
              {activeTab === 'combined' && 'All Channels • '}
              {activeTab === 'amazon' && 'Amazon FBA • '}
              {activeTab === 'shopify' && 'Shopify Direct • '}
              Last {daysFilter} Days
            </p>
          </div>

          <div className="header-actions">
            <Link to="/dashboard" className="btn-secondary">
              <Home size={16} />
              <span>Dashboard</span>
            </Link>

            <button
              onClick={() => {
                fetchAmazonSales(daysFilter, regionFilter);
                fetchShopifySales(daysFilter);
              }}
              disabled={loading}
              className="btn-secondary"
            >
              <RefreshCw size={16} className={loading ? 'loading-spinner' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-main">
        {/* Platform Tabs */}
        <div className="dashboard-card" style={{marginBottom: '1.5rem'}}>
          <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
            <button
              onClick={() => setActiveTab('combined')}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: activeTab === 'combined' ? '#3b82f6' : 'white',
                color: activeTab === 'combined' ? 'white' : '#374151',
                border: `1px solid ${activeTab === 'combined' ? '#3b82f6' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Globe size={16} />
              Combined
            </button>

            <button
              onClick={() => setActiveTab('amazon')}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: activeTab === 'amazon' ? '#ff9900' : 'white',
                color: activeTab === 'amazon' ? 'white' : '#374151',
                border: `1px solid ${activeTab === 'amazon' ? '#ff9900' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Package size={16} />
              Amazon
            </button>

            <button
              onClick={() => setActiveTab('shopify')}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: activeTab === 'shopify' ? '#96bf48' : 'white',
                color: activeTab === 'shopify' ? 'white' : '#374151',
                border: `1px solid ${activeTab === 'shopify' ? '#96bf48' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Store size={16} />
              Shopify
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="dashboard-card" style={{marginBottom: '1.5rem'}}>
          <div style={{display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
              <Filter size={16} style={{color: '#6b7280'}} />
              <span style={{fontSize: '0.875rem', fontWeight: '500', color: '#374151'}}>Filters:</span>
            </div>

        <select
          value={useCustomRange ? 'custom' : daysFilter}
          onChange={(e) => {
            if (e.target.value === 'custom') {
              setUseCustomRange(true);
            } else {
              setUseCustomRange(false);
              setDaysFilter(parseInt(e.target.value));
            }
          }}
          style={{
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.875rem'
          }}
        >
          <option value={7}>Last 7 Days</option>
          <option value={14}>Last 14 Days</option>
          <option value={30}>Last 30 Days</option>
          <option value={60}>Last 60 Days</option>
          <option value="custom">Custom Range</option>
        </select>


            {useCustomRange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="date"
              value={customRange.from}
              onChange={(e) => setCustomRange({ ...customRange, from: e.target.value })}
              style={{
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
            <span>to</span>
            <input
              type="date"
              value={customRange.to}
              onChange={(e) => setCustomRange({ ...customRange, to: e.target.value })}
              style={{
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
            <button
              onClick={() => {
                if (!customRange.from || !customRange.to) {
                  alert("Please select both start and end dates");
                  return;
                }
                setUseCustomRange(true);
                fetchAmazonSales(undefined, regionFilter);
                fetchShopifySales(undefined);
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              Apply
            </button>

          </div>
        )}



            {activeTab !== 'shopify' && (
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                <option value="ALL">All Regions</option>
                <option value="EU">Europe (EU)</option>
                <option value="NA">North America (NA)</option>
              </select>
            )}
          </div>
        </div>

        {/* COMBINED VIEW */}
        {activeTab === 'combined' && (
          <>
            {/* Combined Statistics Cards */}
            <div className="cards-grid" style={{marginBottom: '2rem'}}>
              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Total Revenue</p>
                    <p className="card-value">${combinedStats.total_revenue.toLocaleString()}</p>
                    <p className="card-subtitle">All Channels</p>
                  </div>
                  <div className="card-icon icon-green">
                    <DollarSign size={24} />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Total Orders</p>
                    <p className="card-value">{combinedStats.total_orders.toLocaleString()}</p>
                    <p className="card-subtitle">Combined</p>
                  </div>
                  <div className="card-icon icon-blue">
                    <ShoppingCart size={24} />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Amazon</p>
                    <p className="card-value">${amazonStats.total_revenue.toLocaleString()}</p>
                    <p className="card-subtitle" style={{color: '#ff9900'}}>
                      {combinedStats.amazon_percentage}% of total
                    </p>
                  </div>
                  <div className="card-icon" style={{backgroundColor: '#fff3e0'}}>
                    <Package size={24} style={{color: '#ff9900'}} />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Shopify</p>
                    <p className="card-value">${shopifyStats.total_revenue.toLocaleString()}</p>
                    <p className="card-subtitle" style={{color: '#96bf48'}}>
                      {combinedStats.shopify_percentage}% of total
                    </p>
                  </div>
                  <div className="card-icon" style={{backgroundColor: '#f0f7e0'}}>
                    <Store size={24} style={{color: '#96bf48'}} />
                  </div>
                </div>
              </div>
            </div>

            {/* Channel Comparison Table */}
            <div className="dashboard-card">
              <div className="section-title" style={{marginBottom: '1.5rem'}}>
                <TrendingUp size={20} />
                Channel Performance Comparison
              </div>

              <div style={{overflowX: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{borderBottom: '2px solid #e5e7eb'}}>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Channel</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Orders</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Revenue</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Avg Order</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{borderBottom: '1px solid #f3f4f6'}}>
                      <td style={{padding: '0.75rem'}}>
                        <span style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Package size={16} style={{color: '#ff9900'}} />
                          <strong>Amazon FBA</strong>
                        </span>
                      </td>
                      <td style={{padding: '0.75rem'}}>{amazonStats.total_orders.toLocaleString()}</td>
                      <td style={{padding: '0.75rem', fontWeight: '600'}}>${amazonStats.total_revenue.toLocaleString()}</td>
                      <td style={{padding: '0.75rem'}}>
                        ${amazonStats.total_orders > 0 ? (amazonStats.total_revenue / amazonStats.total_orders).toFixed(2) : '0'}
                      </td>
                      <td style={{padding: '0.75rem'}}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#fff3e0',
                          color: '#ff9900',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: '600'
                        }}>
                          {combinedStats.amazon_percentage}%
                        </span>
                      </td>
                    </tr>
                    <tr style={{borderBottom: '1px solid #f3f4f6'}}>
                      <td style={{padding: '0.75rem'}}>
                        <span style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Store size={16} style={{color: '#96bf48'}} />
                          <strong>Shopify Direct</strong>
                        </span>
                      </td>
                      <td style={{padding: '0.75rem'}}>{shopifyStats.total_orders.toLocaleString()}</td>
                      <td style={{padding: '0.75rem', fontWeight: '600'}}>${shopifyStats.total_revenue.toLocaleString()}</td>
                      <td style={{padding: '0.75rem'}}>${shopifyStats.avg_order_value.toFixed(2)}</td>
                      <td style={{padding: '0.75rem'}}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#f0f7e0',
                          color: '#96bf48',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: '600'
                        }}>
                          {combinedStats.shopify_percentage}%
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* AMAZON VIEW */}
        {activeTab === 'amazon' && (
          <>
            {/* Amazon Statistics Cards */}
            <div className="cards-grid" style={{marginBottom: '2rem'}}>
              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Total Orders</p>
                    <p className="card-value">{amazonStats.total_orders.toLocaleString()}</p>
                    <p className="card-subtitle">Last {daysFilter} days</p>
                  </div>
                  <div className="card-icon icon-blue">
                    <ShoppingCart size={24} />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Units Sold</p>
                    <p className="card-value">{amazonStats.total_units.toLocaleString()}</p>
                    <p className="card-subtitle">{amazonStats.total_orders > 0 ? (amazonStats.total_units / amazonStats.total_orders).toFixed(1) : 0} per order</p>
                  </div>
                  <div className="card-icon icon-green">
                    <Package size={24} />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Total Revenue</p>
                    <p className="card-value">${amazonStats.total_revenue.toLocaleString()}</p>
                    <p className="card-subtitle">Last {daysFilter} days</p>
                  </div>
                  <div className="card-icon icon-purple">
                    <DollarSign size={24} />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Avg Order Value</p>
                    <p className="card-value">${amazonStats.total_orders > 0 ? (amazonStats.total_revenue / amazonStats.total_orders).toFixed(2) : '0'}</p>
                    <p className="card-subtitle">Per order</p>
                  </div>
                  <div className="card-icon icon-yellow">
                    <TrendingUp size={24} />
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Sales by Region with Details Button */}
            <div className="dashboard-card" style={{marginBottom: '2rem'}}>
              <div className="section-title" style={{
                marginBottom: '1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <Package size={20} />
                  Sales by Region
                </div>
                <button
                  onClick={() => fetchAmazonDetailedData('ALL')}
                  style={{
                    padding: '0.375rem 0.75rem',
                    backgroundColor: '#ff9900',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem'
                  }}
                >
                  <Eye size={14} />
                  View Detailed Breakdown
                </button>
              </div>

              <div style={{overflowX: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{borderBottom: '2px solid #e5e7eb'}}>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Region</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Orders</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Units</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Revenue</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Avg Order</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amazonByRegion.map((region, index) => (
                      <tr key={index} style={{borderBottom: '1px solid #f3f4f6'}}>
                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: region.REGION === 'EU' ? '#dbeafe' : '#fef3c7',
                            color: region.REGION === 'EU' ? '#1e40af' : '#92400e',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                            {region.REGION === 'EU' ? 'Europe' : 'North America'}
                          </span>
                        </td>
                        <td style={{padding: '0.75rem', fontWeight: '500'}}>
                          {region.TOTAL_ORDERS?.toLocaleString() || 0}
                        </td>
                        <td style={{padding: '0.75rem', fontWeight: '500'}}>
                          {region.TOTAL_UNITS?.toLocaleString() || 0}
                        </td>
                        <td style={{padding: '0.75rem', fontWeight: '600', color: '#059669'}}>
                          ${region.TOTAL_REVENUE?.toLocaleString() || 0}
                        </td>
                        <td style={{padding: '0.75rem', color: '#6b7280'}}>
                          ${region.TOTAL_ORDERS > 0 ? (region.TOTAL_REVENUE / region.TOTAL_ORDERS).toFixed(2) : '0.00'}
                        </td>
                        <td style={{padding: '0.75rem'}}>
                          <button
                            onClick={() => fetchAmazonDetailedData(region.REGION)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#eff6ff',
                              color: '#2563eb',
                              border: '1px solid #dbeafe',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Daily Sales */}
            <div className="dashboard-card">
              <div className="section-title" style={{marginBottom: '1.5rem'}}>
                <Calendar size={20} />
                Daily Sales (Last {Math.min(daysFilter, 30)} Days)
              </div>

              <div style={{overflowX: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{borderBottom: '2px solid #e5e7eb'}}>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Date</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Region</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Orders</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Units</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151'}}>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amazonDailySales.slice(0, 30).map((sale, index) => (
                      <tr key={index} style={{borderBottom: '1px solid #f3f4f6'}}>
                        <td style={{padding: '0.75rem', fontWeight: '500'}}>
                          {new Date(sale.SALE_DATE).toLocaleDateString()}
                        </td>
                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: sale.REGION === 'EU' ? '#dbeafe' : '#fef3c7',
                            color: sale.REGION === 'EU' ? '#1e40af' : '#92400e',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            {sale.REGION}
                          </span>
                        </td>
                        <td style={{padding: '0.75rem'}}>
                          {sale.TOTAL_ORDERS?.toLocaleString() || 0}
                        </td>
                        <td style={{padding: '0.75rem'}}>
                          {sale.TOTAL_UNITS?.toLocaleString() || 0}
                        </td>
                        <td style={{padding: '0.75rem', fontWeight: '600', color: '#059669'}}>
                          ${sale.TOTAL_REVENUE?.toLocaleString() || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* SHOPIFY VIEW */}
        {activeTab === 'shopify' && (
          <>
            {/* Shopify Statistics Cards */}
            <div className="cards-grid" style={{marginBottom: '2rem'}}>
              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Total Orders</p>
                    <p className="card-value">{shopifyStats.total_orders.toLocaleString()}</p>
                    <p className="card-subtitle">Direct Sales</p>
                  </div>
                  <div className="card-icon icon-blue">
                    <ShoppingCart size={24} />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Total Revenue</p>
                    <p className="card-value">${shopifyStats.total_revenue.toLocaleString()}</p>
                    <p className="card-subtitle">Shopify Store</p>
                  </div>
                  <div className="card-icon icon-green">
                    <DollarSign size={24} />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Unique Customers</p>
                    <p className="card-value">{shopifyStats.unique_customers.toLocaleString()}</p>
                    <p className="card-subtitle">Active Buyers</p>
                  </div>
                  <div className="card-icon icon-purple">
                    <Users size={24} />
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-content">
                  <div className="card-info">
                    <p className="card-title">Avg Order Value</p>
                    <p className="card-value">${shopifyStats.avg_order_value.toFixed(2)}</p>
                    <p className="card-subtitle">Per Order</p>
                  </div>
                  <div className="card-icon icon-yellow">
                    <TrendingUp size={24} />
                  </div>
                </div>
              </div>
            </div>

            {/* Top Products */}
            {shopifyTopProducts.length > 0 && (
              <div className="dashboard-card" style={{marginBottom: '2rem'}}>
                <div className="section-title" style={{marginBottom: '1.5rem'}}>
                  <Package size={20} />
                  Top Products - Shopify
                </div>

                <div style={{overflowX: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse'}}>
                    <thead>
                      <tr style={{borderBottom: '2px solid #e5e7eb'}}>
                        <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Product</th>
                        <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>SKU</th>
                        <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Orders</th>
                        <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Units Sold</th>
                        <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shopifyTopProducts.map((product, index) => (
                        <tr key={index} style={{borderBottom: '1px solid #f3f4f6'}}>
                          <td style={{padding: '0.75rem', fontWeight: '500'}}>
                            {product.PRODUCT_NAME}
                          </td>
                          <td style={{padding: '0.75rem'}}>
                            <span style={{
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              backgroundColor: '#f3f4f6',
                              padding: '0.125rem 0.375rem',
                              borderRadius: '4px'
                            }}>
                              {product.SKU}
                            </span>
                          </td>
                          <td style={{padding: '0.75rem'}}>{product.ORDER_COUNT}</td>
                          <td style={{padding: '0.75rem'}}>{product.UNITS_SOLD}</td>
                          <td style={{padding: '0.75rem', fontWeight: '600', color: '#059669'}}>
                            ${parseFloat(product.NET_REVENUE || product.GROSS_REVENUE || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Shopify Daily Sales with View Orders Button */}
            <div className="dashboard-card">
              <div className="section-title" style={{marginBottom: '1.5rem'}}>
                <Calendar size={20} />
                Daily Sales - Shopify (Last 10 Days)
              </div>

              <div style={{overflowX: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{borderBottom: '2px solid #e5e7eb'}}>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Date</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Orders</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Revenue</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Customers</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Avg Order</th>
                      <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shopifyDailySales.slice(0, 10).map((day, index) => (
                      <tr key={index} style={{borderBottom: '1px solid #f3f4f6'}}>
                        <td style={{padding: '0.75rem', fontWeight: '500'}}>
                          {new Date(day.SALE_DATE).toLocaleDateString()}
                        </td>
                        <td style={{padding: '0.75rem'}}>{day.ORDER_COUNT}</td>
                        <td style={{padding: '0.75rem', fontWeight: '600', color: '#059669'}}>
                          ${parseFloat(day.NET_REVENUE || 0).toLocaleString()}
                        </td>
                        <td style={{padding: '0.75rem'}}>{day.UNIQUE_CUSTOMERS}</td>
                        <td style={{padding: '0.75rem'}}>
                          ${parseFloat(day.AVG_ORDER_VALUE || 0).toFixed(2)}
                        </td>
                        <td style={{padding: '0.75rem'}}>
                          {day.ORDER_COUNT > 0 && (
                            <button
                              onClick={() => handleShowOrders(day.SALE_DATE)}
                              style={{
                                padding: '0.375rem 0.75rem',
                                backgroundColor: '#eff6ff',
                                color: '#2563eb',
                                border: '1px solid #dbeafe',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem'
                              }}
                            >
                              <Eye size={14} />
                              View Orders
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {shopifyDailySales.length === 0 && !loading && (
                  <div className="empty-state" style={{
                    textAlign: 'center',
                    padding: '2rem',
                    color: '#6b7280'
                  }}>
                    <Calendar size={48} style={{opacity: 0.5, margin: '0 auto 0.75rem'}} />
                    <p>No Shopify sales data found</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Orders Modal */}
      {OrdersModal()}

      {/* Amazon Details Modal */}
      {showAmazonDetails && amazonDetailedData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '85vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#ff9900',
              color: 'white'
            }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div>
                  <h2 style={{margin: 0, fontSize: '1.5rem', fontWeight: '600'}}>
                    Amazon FBA Detailed Sales Analysis
                  </h2>
                  <p style={{margin: '0.25rem 0 0 0', fontSize: '0.875rem', opacity: 0.9}}>
                    Last {daysFilter} days breakdown
                  </p>
                </div>
                <button
                  onClick={() => setShowAmazonDetails(false)}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    border: '2px solid white',
                    backgroundColor: 'transparent',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '1.25rem',
                    fontWeight: '500'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{flex: 1, padding: '1.5rem', overflowY: 'auto'}}>
              {/* Summary Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#dbeafe',
                  borderRadius: '8px',
                  border: '1px solid #93c5fd'
                }}>
                  <h4 style={{margin: 0, color: '#1e40af', fontSize: '0.875rem'}}>Europe (EU)</h4>
                  <p style={{margin: '0.5rem 0 0 0', fontSize: '1.25rem', fontWeight: '600', color: '#1d4ed8'}}>
                    ${amazonDetailedData.summary?.EU?.revenue?.toLocaleString() || 0}
                  </p>
                  <p style={{margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#3730a3'}}>
                    {amazonDetailedData.summary?.EU?.orders || 0} orders • {amazonDetailedData.summary?.EU?.units || 0} units
                  </p>
                </div>

                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  border: '1px solid #fcd34d'
                }}>
                  <h4 style={{margin: 0, color: '#92400e', fontSize: '0.875rem'}}>North America (NA)</h4>
                  <p style={{margin: '0.5rem 0 0 0', fontSize: '1.25rem', fontWeight: '600', color: '#b45309'}}>
                    ${amazonDetailedData.summary?.NA?.revenue?.toLocaleString() || 0}
                  </p>
                  <p style={{margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#78350f'}}>
                    {amazonDetailedData.summary?.NA?.orders || 0} orders • {amazonDetailedData.summary?.NA?.units || 0} units
                  </p>
                </div>

                <div style={{
                  padding: '1rem',
                  backgroundColor: '#dcfce7',
                  borderRadius: '8px',
                  border: '1px solid #86efac'
                }}>
                  <h4 style={{margin: 0, color: '#166534', fontSize: '0.875rem'}}>Total Combined</h4>
                  <p style={{margin: '0.5rem 0 0 0', fontSize: '1.25rem', fontWeight: '600', color: '#15803d'}}>
                    ${amazonDetailedData.summary?.total?.revenue?.toLocaleString() || 0}
                  </p>
                  <p style={{margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#14532d'}}>
                    {amazonDetailedData.summary?.total?.orders || 0} orders • {amazonDetailedData.summary?.total?.units || 0} units
                  </p>
                </div>
              </div>

              {/* Products Table */}
              <div style={{marginBottom: '2rem'}}>
                <h3 style={{marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600'}}>
                  Top Products by Stock Level
                </h3>
                <div style={{overflowX: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem'}}>
                    <thead>
                      <tr style={{borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb'}}>
                        <th style={{padding: '0.75rem', textAlign: 'left'}}>Region</th>
                        <th style={{padding: '0.75rem', textAlign: 'left'}}>SKU</th>
                        <th style={{padding: '0.75rem', textAlign: 'left'}}>ASIN</th>
                        <th style={{padding: '0.75rem', textAlign: 'left'}}>Available</th>
                        <th style={{padding: '0.75rem', textAlign: 'left'}}>Reserved</th>
                        <th style={{padding: '0.75rem', textAlign: 'left'}}>Total Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amazonDetailedData.products?.slice(0, 20).map((product, idx) => (
                        <tr key={idx} style={{borderBottom: '1px solid #f3f4f6'}}>
                          <td style={{padding: '0.75rem'}}>
                            <span style={{
                              padding: '0.125rem 0.375rem',
                              backgroundColor: product.REGION === 'EU' ? '#dbeafe' : '#fef3c7',
                              color: product.REGION === 'EU' ? '#1e40af' : '#92400e',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              {product.REGION}
                            </span>
                          </td>
                          <td style={{padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.75rem'}}>
                            {product.SKU}
                          </td>
                          <td style={{padding: '0.75rem', color: '#6b7280', fontSize: '0.75rem'}}>
                            {product.ASIN}
                          </td>
                          <td style={{padding: '0.75rem', fontWeight: '500'}}>
                            {product.CURRENT_STOCK || 0}
                          </td>
                          <td style={{padding: '0.75rem'}}>
                            {product.RESERVED_UNITS || 0}
                          </td>
                          <td style={{padding: '0.75rem', fontWeight: '600', color: '#059669'}}>
                            {product.TOTAL_STOCK || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          {/* Modal Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowAmazonDetails(false)}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;